import {
  Component,
  OnInit,
  Input,
  Renderer2,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter
} from '@angular/core';
import { VehicleOption } from 'remarketing-angular-library';
import { sortBy } from 'lodash';
import { subGroupMappings } from '../../data/option-sub-group-mapping';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntil } from 'rxjs/operators';
import { SaComponent } from 'smartauction-frontend-micro-library';
import { FormsModule } from '@angular/forms';


@Component({
    selector: 'app-options-and-accessories-form',
    templateUrl: './options-and-accessories-form.component.html',
    styleUrls: ['./options-and-accessories-form.component.scss'],
    imports: [FormsModule]
})
export class OptionsAndAccessoriesFormComponent extends SaComponent implements OnInit {
  @Input() set otherOptions(value: VehicleOption[]) {
    this._otherOptions = this.sortOptionsArray(value);
    this.setupOptions();
  }
  @Output() onUpdateInstalledOptions = new EventEmitter<VehicleOption[]>();
  @Output() onRemoveOptionUpdateAvailableOptions = new EventEmitter<VehicleOption>();
  @ViewChild('toggleDropdownButton') toggleDropdownButton: ElementRef;
  @ViewChild('optionsAndAccessoriesPopover') optionsAndAccessoriesPopover: ElementRef;
  _otherOptions: VehicleOption[];
  availableOptions: VehicleOption[];
  installedOptions: VehicleOption[];
  dropdownOptions: VehicleOption[];
  optionsGroups: any;
  isOpen: boolean;
  rendererClickListener: any;

  optionsAndAccessoriesFormTranslations = {
    textAddOption: '',
    labelAvailableOptions: '',
    textInstalledOptions: '',
    textUpdateInstalledOptions: '',
    textManageInstalledOptions: '',
    textIdentifyOption: '',
    labelAftermarket: '',
    labelRemove: '',
    ariaLabelremove: '',
    labelSearch: ''
  };

  constructor(private readonly renderer: Renderer2, private readonly translocoService: TranslocoService) {
    super();
    this.rendererClickListener = this.renderer.listen('window', 'mousedown', (e: Event) => {
      if (
        !this.toggleDropdownButton.nativeElement.contains(e.target) &&
        this.optionsAndAccessoriesPopover && !this.optionsAndAccessoriesPopover.nativeElement.contains(e.target)
      ) {
        this.isOpen = false;
        this.setDropdownOptions();
        this.setOptionsGroups();
      }
    });
  }

  ngOnInit() {
    this.getTranslations();
    this.setupOptions();
  }

  ngOnDestroy() {
    this.rendererClickListener();
  }

  setupOptions() {
    this.setAvailableOptions();
    this.setDropdownOptions();
    this.setOptionsGroups();
    this.installedOptions = sortBy(
      this.availableOptions.filter(o => o.installed === true),
      ['userFriendlyDescription']
    );
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }

  search(e) {
    this.setDropdownOptions();

    const value = e.target.value.toLowerCase().trim();

    this.dropdownOptions = this.dropdownOptions.filter(o =>
      o.userFriendlyDescription
        .toLowerCase()
        .trim()
        .includes(value)
    );

    this.setOptionsGroups();
  }

  onInstalledOptionChanged(option: VehicleOption) {
    this.availableOptions = this.availableOptions.map(o => {
      if (option.optionCode === o.optionCode) {
        o.installed = !o.installed;
      }

      return o;
    });
  }

  onRemoveInstalledOption(option: VehicleOption) {
    option = { ...option, afterMarket: false };
    this.onInstalledOptionChanged(option);

    this.setInstalledOptions();
    this.onRemoveOptionUpdateAvailableOptions.emit(option);
  }

  setAvailableOptions() {
    this.availableOptions = this._otherOptions || [];
  }

  setDropdownOptions() {
    this.dropdownOptions = this._otherOptions || [];
  }

  setInstalledOptions() {
    this.installedOptions = sortBy(
      this.availableOptions.filter(o => o.installed === true),
      ['userFriendlyDescription']
    );
    this.onUpdateInstalledOptions.emit(this.installedOptions);
  }

  setOptionsGroups() {
    this.optionsGroups = this.dropdownOptions.reduce((acc, currVal) => {
      let group = acc.find(g => g.subGroupName === currVal.subGroupName);

      if (!group) {
        group = {};
        group.subGroupName = currVal.subGroupName;
        group.options = [];
        acc.push(group);
      }

      group.options.push(currVal);

      return acc.map(g => {
        if (g.subGroupName === currVal.subGroupName) {
          g = group;
        }

        return g;
      });
    }, []);
  }

  getSubGroupDisplayName(subGroup: string) {
    const mapping = subGroupMappings.find(x => x.subGroupName === subGroup);
    return mapping ? mapping.displayName : '';
  }

  // Sorts options by their Display Group, then the actual description
  sortOptionsArray(options: VehicleOption[]) {
    return options?.sort((a, b) => {
      return (
        this.getSubGroupDisplayName(a.subGroupName).localeCompare(this.getSubGroupDisplayName(b.subGroupName)) ||
        a.userFriendlyDescription.localeCompare(b.userFriendlyDescription)
      );
    });
  }

  getTranslations() {
    const keyParams = {
      'sellModule.posting.optionAndAccessoriesForm': {},
      common: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(super.destroyed$))
      .subscribe(translate => {
        this.optionsAndAccessoriesFormTranslations.textAddOption = translate[0].textAddOption;
        this.optionsAndAccessoriesFormTranslations.labelAvailableOptions = translate[0].labelAvailableOptions;
        this.optionsAndAccessoriesFormTranslations.textInstalledOptions = translate[0].textInstalledOptions;
        this.optionsAndAccessoriesFormTranslations.textUpdateInstalledOptions = translate[0].textUpdateInstalledOptions;
        this.optionsAndAccessoriesFormTranslations.textManageInstalledOptions = translate[0].textManageInstalledOptions;
        this.optionsAndAccessoriesFormTranslations.textIdentifyOption = translate[0].textIdentifyOption;
        this.optionsAndAccessoriesFormTranslations.labelAftermarket = translate[0].labelAftermarket;
        this.optionsAndAccessoriesFormTranslations.labelRemove = translate[0].labelRemove;
        this.optionsAndAccessoriesFormTranslations.ariaLabelremove = translate[0].ariaLabelremove;
        this.optionsAndAccessoriesFormTranslations.labelSearch = translate[1].labelSearch;
      });
  }
}
